import {
  MetaProps,
  BENZINGA_LOGO_URL,
  JsonLd,
  buildBreadcrumbListNode,
  formatStringToValidJsonString,
  isRenderableBreadcrumbTrail,
  parseSpeakableValue,
} from '@benzinga/seo';
import { fixedEncodeURI } from '@benzinga/utils';

const logoPNG = 'https://www.benzinga.com/next-assets/images/schema-publisher-logo-benzinga.png';
const publisherLogoWidth = 625;
const publisherLogoHeight = 88;

const decodeDescription = (text: string) => {
  return text ?? '';
};

const organizationMetaSchema = () => {
  return {
    '@type': 'Organization',
    logo: {
      '@type': 'ImageObject',
      url: `${logoPNG}`,
      width: publisherLogoWidth,
      height: publisherLogoHeight,
    },
    name: 'Benzinga',
    url: 'https://www.benzinga.com',
  };
};

const truncateStructuredDataHeadline = (text: string | null | undefined, maxLength = 110): string => {
  if (!text) {
    return '';
  }

  const cleaned = text.replace(/(\r\n|\n|\r)/gm, ' ').trim();
  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  const truncated = cleaned.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  const cutIndex = lastSpace > maxLength * 0.6 ? lastSpace : maxLength;
  return `${truncated.slice(0, cutIndex).trimEnd()}…`;
};

function structuredDataPage({
  author,
  authorURL,
  canonical,
  dateCreated,
  dateUpdated,
  description,
  image,
  isNotAccessibleForFree,
  structuredData,
  title,
}: MetaProps) {
  author = author || 'Benzinga';
  const omitDates = structuredData?.omitDates ?? false;
  if (!omitDates) {
    dateCreated = dateCreated || '2010-01-01T00:00:00Z';
    dateUpdated = dateUpdated || '2010-01-01T00:00:00Z';
  }
  image = image || BENZINGA_LOGO_URL;
  const pageType = structuredData?.pageType || 'WebPage';
  const isCollection = pageType === 'CollectionPage';
  const keywords = structuredData?.keywords || [];
  const mentions = structuredData?.mentions || [];
  const breadcrumb = structuredData?.breadcrumb;

  title = truncateStructuredDataHeadline(title);

  const cleanQuote = (text: string | null) => {
    return text ? text.replace(/"/g, '&quot;') : '';
  };

  const convertQuoteToSingle = (text: string | null) => {
    return text ? text.replace(/"/g, `'`) : '';
  };

  const articleBody = structuredData?.articleBody ? formatStringToValidJsonString(structuredData.articleBody) : '';
  const speakable = structuredData?.speakable
    ? parseSpeakableValue(structuredData.speakable)
    : undefined;

  const authorPerson = {
    '@type': 'Person',
    name: cleanQuote(author),
    ...(authorURL && { url: authorURL }),
  };

  const headline = cleanQuote(title)?.replace(/(\r\n|\n|\r)/gm, ' ');

  return {
    '@context': 'https://schema.org',
    '@type': pageType,
    ...(!isCollection && { author: authorPerson }),
    ...(!omitDates && !isCollection && {
      dateCreated: dateCreated ?? null,
      dateModified: dateUpdated ?? null,
      datePublished: dateCreated ?? null,
    }),
    description: convertQuoteToSingle(decodeDescription(description)),
    ...(isCollection ? { name: headline } : { headline }),
    image: fixedEncodeURI(image.toString()) ?? null,
    keywords: keywords,
    ...(pageType !== 'ProfilePage' && !isCollection && {
      mainEntityOfPage: {
        '@id': canonical,
        '@type': 'WebPage',
      },
    }),
    ...(isRenderableBreadcrumbTrail(breadcrumb) && {
      breadcrumb: buildBreadcrumbListNode(breadcrumb),
    }),
    mentions: mentions,
    publisher: organizationMetaSchema(),
    url: canonical,
    ...(structuredData?.mainEntity !== undefined && {
      mainEntity: structuredData.mainEntity,
    }),
    isAccessibleForFree: isNotAccessibleForFree ? false : true,
    ...(structuredData?.articleBody && { articleBody }),
    ...(structuredData?.about && { about: structuredData.about }),
    ...(structuredData?.articleSection && { articleSection: structuredData.articleSection }),
    ...(structuredData?.video && { video: structuredData.video }),
    ...(speakable && { speakable }),
    ...(isNotAccessibleForFree && {
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: '.paywall-content',
      },
    }),
    ...(pageType === 'NewsArticle' && {
      isPartOf: {
        '@type': ['CreativeWork', 'Product'],
        name: 'Benzinga',
        productID: 'CAowneCICw:openaccess',
      },
    }),
  };
}

export function PageStructuredData({ metaProps }: { metaProps: MetaProps }) {
  return <JsonLd data={structuredDataPage(metaProps)} id="page-structured-data" />;
}
